import React, { createContext, useContext, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Global navigation guard. Pages that have unsaved state can register a
 * guard function returning a Promise<boolean> — true to proceed with the
 * navigation, false to cancel. Any component wishing to navigate away
 * should call `guardedNavigate(path)` (or the provided `guard()` helper
 * before running a non-navigate action like logout).
 *
 * This works with <BrowserRouter> since it doesn't rely on the data-router
 * useBlocker hook. Downside: only in-app flows that honour the guard are
 * protected (we route sidebar NavLinks and logout through it). Browser
 * close / refresh / back-button is handled separately by `beforeunload` in
 * the editor itself.
 */
const NavGuardContext = createContext(null);

export function NavGuardProvider({ children }) {
  const guardRef = useRef(null);
  const navigate = useNavigate();

  const registerGuard = useCallback((fn) => {
    guardRef.current = fn;
    return () => {
      if (guardRef.current === fn) guardRef.current = null;
    };
  }, []);

  // Return true if navigation should proceed, false if it was cancelled.
  const guard = useCallback(async () => {
    const fn = guardRef.current;
    if (!fn) return true;
    try {
      return await fn();
    } catch {
      return true;  // on error, don't trap the user
    }
  }, []);

  const guardedNavigate = useCallback(
    async (path, opts) => {
      const ok = await guard();
      if (ok) navigate(path, opts);
    },
    [guard, navigate]
  );

  return (
    <NavGuardContext.Provider value={{ registerGuard, guard, guardedNavigate }}>
      {children}
    </NavGuardContext.Provider>
  );
}

export function useNavGuard() {
  const ctx = useContext(NavGuardContext);
  if (!ctx) {
    // Fail-soft: if the provider isn't mounted yet, behave like a
    // plain useNavigate without any blocking.
    return {
      registerGuard: () => () => {},
      guard: async () => true,
      guardedNavigate: () => {},
    };
  }
  return ctx;
}
